import {
  EventId,
  MessageId,
  type OrchestrationMessage,
  type OrchestrationThreadActivity,
} from "@t3tools/contracts";

import type { Agent, AgentRun } from "../../morrowAgents";

const now = Date.now();
const minutesAgo = (m: number) => new Date(now - m * 60_000).toISOString();
const hoursAgo = (h: number) => new Date(now - h * 3_600_000).toISOString();

export const MOCK_DEMO_AGENT_ID = "agent_mock_mira";

export interface MockAgentMessage {
  readonly id: string;
  readonly role: "user" | "assistant" | "system";
  readonly text: string;
  readonly createdAt: string;
  readonly streaming?: boolean;
}

interface MockAgentContent {
  readonly messages: readonly MockAgentMessage[];
  readonly activities: readonly OrchestrationThreadActivity[];
  readonly runs: readonly AgentRun[];
}

function toolActivity(
  id: string,
  createdAt: string,
  summary: string,
  toolName: string,
  status: "completed" | "inProgress" = "completed",
): OrchestrationThreadActivity {
  return {
    id: EventId.make(id),
    tone: "tool",
    kind: status === "inProgress" ? "tool.started" : "tool.completed",
    summary,
    payload: { toolName, status },
    turnId: null,
    createdAt,
  };
}

function baseAgent(
  partial: Pick<Agent, "id" | "name" | "role" | "description" | "avatar" | "autonomy" | "status"> &
    Partial<
      Pick<
        Agent,
        | "allowedApps"
        | "capabilities"
        | "scheduleCron"
        | "instruction"
        | "homeThreadId"
        | "updatedAt"
      >
    >,
): Agent {
  return {
    voice: null,
    instruction: partial.instruction ?? `You are ${partial.name}.`,
    identityKind: "managed",
    identityAddress: null,
    allowedApps: partial.allowedApps ?? null,
    capabilities: partial.capabilities ?? { cloudBrowser: true, vault: true, memoryWrite: true },
    mayTaskAgents: null,
    limits: null,
    budget: null,
    triggers: null,
    scheduleCron: partial.scheduleCron ?? null,
    keepWorking: 0,
    charterVersion: 1,
    tags: [],
    homeThreadId: partial.homeThreadId ?? `thread_mock_${partial.id}`,
    updatedAt: partial.updatedAt ?? minutesAgo(0),
    ...partial,
  };
}

const MIRA_CONTENT: MockAgentContent = {
  messages: [
    {
      id: "mock-mira-1",
      role: "assistant",
      text: "Günaydın! Ben **Mira** — satış hattındayım. Bugün pipeline’da **Acme**, **Globex** ve yeni **Northwind** lead’i var.",
      createdAt: minutesAgo(42),
    },
    {
      id: "mock-mira-2",
      role: "user",
      text: "Acme için son teklif durumunu özetler misin? Slack’te bir şey kaçırdıysak söyle.",
      createdAt: minutesAgo(38),
    },
    {
      id: "mock-mira-3",
      role: "assistant",
      text: "Acme’ye teklif **12 gün önce** gitti. CRM notu: *fiyat onayı* bekleniyor. Slack’te #sales’te dün Sarah “CFO haftaya döner” demiş — ben de Gmail’de thread’i taradım, yeni mail yok.",
      createdAt: minutesAgo(36),
    },
    {
      id: "mock-mira-4",
      role: "user",
      text: "Kibar bir hatırlatma taslağı yaz, İngilizce.",
      createdAt: minutesAgo(34),
    },
    {
      id: "mock-mira-5",
      role: "assistant",
      text: "Taslak hazır:\n\n> Hi team — checking whether you had a chance to review our proposal. Happy to jump on a quick call this week if helpful.\n\nOnayına sunmadan göndermiyorum (`approval` modundayım).",
      createdAt: minutesAgo(32),
    },
  ],
  activities: [
    toolActivity(
      "mock-mira-a1",
      minutesAgo(37),
      "COMPOSIO_SEARCH_WEB Acme Corp pricing news",
      "COMPOSIO_SEARCH_WEB",
    ),
    toolActivity(
      "mock-mira-a2",
      minutesAgo(35),
      "Gmail — thread: Acme proposal follow-up",
      "GMAIL_FETCH_EMAILS",
    ),
    toolActivity(
      "mock-mira-a3",
      minutesAgo(33),
      "Slack — read #sales last 24h",
      "SLACK_FETCH_MESSAGES",
    ),
    {
      id: EventId.make("mock-mira-a4"),
      tone: "approval",
      kind: "tool.updated",
      summary: "Send email draft — waiting for your OK",
      payload: { toolName: "GMAIL_SEND_EMAIL", status: "inProgress" as const },
      turnId: null,
      createdAt: minutesAgo(31),
    },
  ],
  runs: [
    {
      id: "mock-run-mira-1",
      agentId: MOCK_DEMO_AGENT_ID,
      source: "schedule",
      detail: "Morning pipeline digest",
      status: "completed",
      error: null,
      threadId: "thread_mock_agent_mira",
      createdAt: hoursAgo(2),
    },
    {
      id: "mock-run-mira-2",
      agentId: MOCK_DEMO_AGENT_ID,
      source: "message",
      detail: "Acme follow-up research",
      status: "completed",
      error: null,
      threadId: "thread_mock_agent_mira",
      createdAt: minutesAgo(40),
    },
  ],
};

const DENIZ_ID = "agent_mock_deniz";
const DENIZ_CONTENT: MockAgentContent = {
  messages: [
    {
      id: "mock-deniz-1",
      role: "assistant",
      text: "Selam, **Deniz** burada — WhatsApp ve destek kanallarını izliyorum.",
      createdAt: minutesAgo(28),
    },
    {
      id: "mock-deniz-2",
      role: "user",
      text: "Müşteri 0544… numarasından “fatura PDF gelmedi” yazmış. Bakar mısın?",
      createdAt: minutesAgo(25),
    },
    {
      id: "mock-deniz-3",
      role: "assistant",
      text: "WhatsApp’tan mesajı eşledim — **Ayşe K.**, son fatura **14 Şubat**. Vault’ta PDF var; linki WhatsApp’tan paylaşmadan önce onay isteyeceğim.",
      createdAt: minutesAgo(22),
    },
    {
      id: "mock-deniz-4",
      role: "assistant",
      text: "Önerilen yanıt:\n\n> Merhaba Ayşe, PDF’i şimdi iletiyorum. Bir sorun olursa yazman yeterli.",
      createdAt: minutesAgo(20),
    },
  ],
  activities: [
    toolActivity(
      "mock-deniz-a1",
      minutesAgo(24),
      "WhatsApp — match customer thread",
      "WHATSAPP_SEARCH",
    ),
    toolActivity(
      "mock-deniz-a2",
      minutesAgo(23),
      "Vault — invoice_FEB-2026.pdf",
      "VAULT_READ_FILE",
    ),
    toolActivity(
      "mock-deniz-a3",
      minutesAgo(21),
      "Draft WhatsApp reply",
      "WHATSAPP_SEND_MESSAGE",
      "inProgress",
    ),
  ],
  runs: [
    {
      id: "mock-run-deniz-1",
      agentId: DENIZ_ID,
      source: "comms",
      detail: "WhatsApp queue sweep",
      status: "completed",
      error: null,
      threadId: "thread_mock_agent_deniz",
      createdAt: hoursAgo(1),
    },
  ],
};

const ECE_ID = "agent_mock_ece";
const ECE_CONTENT: MockAgentContent = {
  messages: [
    {
      id: "mock-ece-1",
      role: "user",
      text: "Bu hafta geciken faturaları listele ve tahsilat maili taslağı çıkar.",
      createdAt: minutesAgo(55),
    },
    {
      id: "mock-ece-2",
      role: "assistant",
      text: "**3 geciken fatura** buldum (Toplam **₺184.200**):\n\n1. **Globex** — 9 gün\n2. **Umbrella Co** — 5 gün\n3. **Initech** — 2 gün\n\nHepsi için tek mail mi, ayrı ayrı mı istersin?",
      createdAt: minutesAgo(52),
    },
    {
      id: "mock-ece-3",
      role: "user",
      text: "Globex için ayrı, diğer ikisi tek mail.",
      createdAt: minutesAgo(50),
    },
    {
      id: "mock-ece-4",
      role: "assistant",
      text: "Globex taslağı daha resmi tonla hazır. Umbrella + Initech için nazik hatırlatma birleştirdim — gönderimden önce **Finance** Slack’ine de özet atacağım.",
      createdAt: minutesAgo(48),
    },
  ],
  activities: [
    toolActivity(
      "mock-ece-a1",
      minutesAgo(54),
      "Accounting export — overdue invoices",
      "ACCOUNTING_LIST",
    ),
    toolActivity(
      "mock-ece-a2",
      minutesAgo(51),
      "Compose Globex collection email",
      "GMAIL_CREATE_DRAFT",
    ),
    toolActivity("mock-ece-a3", minutesAgo(49), "Slack — notify #finance", "SLACK_POST_MESSAGE"),
  ],
  runs: [
    {
      id: "mock-run-ece-1",
      agentId: ECE_ID,
      source: "schedule",
      detail: "Weekly overdue scan",
      status: "completed",
      error: null,
      threadId: "thread_mock_agent_ece",
      createdAt: hoursAgo(3),
    },
  ],
};

const ATLAS_ID = "agent_mock_atlas";
const ATLAS_CONTENT: MockAgentContent = {
  messages: [
    {
      id: "mock-atlas-1",
      role: "user",
      text: "Rakip X’in yeni fiyatlandırmasını bul, kısa brif yaz.",
      createdAt: minutesAgo(75),
    },
    {
      id: "mock-atlas-2",
      role: "assistant",
      text: "Cloud browser ile rakip sitesine baktım. **Pro** plan %12 zam, **Enterprise**’ta kullanıcı başı minimum 50 koltuk. Brif vault’a `research/competitor-x-q1.md` olarak kaydedildi.",
      createdAt: minutesAgo(68),
    },
    {
      id: "mock-atlas-3",
      role: "assistant",
      text: "**Özet:** Zam agresif ama enterprise’da koltuk tabanı yükselmiş — bizim mid-market hikâyesi hâlâ güçlü. Detaylar dosyada.",
      createdAt: minutesAgo(65),
    },
  ],
  activities: [
    toolActivity(
      "mock-atlas-a1",
      minutesAgo(72),
      "cloud_browser — https://competitor-x.example/pricing",
      "cloud_browser",
    ),
    toolActivity(
      "mock-atlas-a2",
      minutesAgo(70),
      "COMPOSIO_SEARCH_WEB Competitor X press release",
      "COMPOSIO_SEARCH_WEB",
    ),
    toolActivity(
      "mock-atlas-a3",
      minutesAgo(69),
      "Vault — write research/competitor-x-q1.md",
      "VAULT_WRITE_FILE",
    ),
  ],
  runs: [
    {
      id: "mock-run-atlas-1",
      agentId: ATLAS_ID,
      source: "delegation",
      detail: "Competitive pricing scan",
      status: "completed",
      error: null,
      threadId: "thread_mock_agent_atlas",
      createdAt: minutesAgo(74),
    },
  ],
};

const NOVA_ID = "agent_mock_nova";
const NOVA_CONTENT: MockAgentContent = {
  messages: [
    {
      id: "mock-nova-1",
      role: "assistant",
      text: "**Nova** — içerik ve sosyal takvim agent’ı. Şu an **duraklatıldım**; yeniden açınca kaldığım yerden devam ederim.",
      createdAt: minutesAgo(120),
    },
    {
      id: "mock-nova-2",
      role: "user",
      text: "LinkedIn için product launch post taslağı vardı, nerede kaldık?",
      createdAt: minutesAgo(118),
    },
    {
      id: "mock-nova-3",
      role: "assistant",
      text: "Taslak vault’ta: **3 varyant** (kısa / hikâye / teknik). Görsel brief’i bekliyorduk — design Slack’te hâlâ açık.",
      createdAt: minutesAgo(115),
    },
  ],
  activities: [
    toolActivity("mock-nova-a1", minutesAgo(117), "Vault — list launch-post drafts", "VAULT_LIST"),
    toolActivity(
      "mock-nova-a2",
      minutesAgo(116),
      "Slack — check #design thread",
      "SLACK_FETCH_MESSAGES",
    ),
  ],
  runs: [
    {
      id: "mock-run-nova-1",
      agentId: NOVA_ID,
      source: "test",
      detail: "Launch post variants",
      status: "completed",
      error: null,
      threadId: "thread_mock_agent_nova",
      createdAt: hoursAgo(5),
    },
  ],
};

export const MOCK_AGENTS: readonly Agent[] = [
  baseAgent({
    id: MOCK_DEMO_AGENT_ID,
    name: "Mira",
    role: "Satış asistanı",
    description: "Pipeline, teklif takibi, Gmail ve Slack üzerinden lead nurture.",
    avatar: "/brand/agents/purple.png",
    autonomy: "approval",
    status: "active",
    allowedApps: ["gmail", "slack", "hubspot"],
    homeThreadId: "thread_mock_agent_mira",
  }),
  baseAgent({
    id: DENIZ_ID,
    name: "Deniz",
    role: "Destek & WhatsApp",
    description: "Müşteri mesajlarını yanıtlar, fatura ve teslimat sorularını çözer.",
    avatar: "/brand/agents/blue.png",
    autonomy: "approval",
    status: "active",
    allowedApps: ["whatsapp", "gmail", "vault"],
    homeThreadId: "thread_mock_agent_deniz",
  }),
  baseAgent({
    id: ECE_ID,
    name: "Ece",
    role: "Finans operasyonları",
    description: "Geciken faturalar, tahsilat taslakları, finance ekibi özeti.",
    avatar: "/brand/agents/green.png",
    autonomy: "autonomous",
    status: "active",
    allowedApps: ["gmail", "slack", "accounting"],
    scheduleCron: "0 9 * * 1-5",
    homeThreadId: "thread_mock_agent_ece",
  }),
  baseAgent({
    id: ATLAS_ID,
    name: "Atlas",
    role: "Araştırma",
    description: "Web tarama, rakip analizi, vault’a yapılandırılmış brif.",
    avatar: "/brand/agents/orange.png",
    autonomy: "autonomous",
    status: "active",
    allowedApps: ["browser", "vault"],
    capabilities: { cloudBrowser: true, browser: true, vault: true, memoryWrite: true },
    homeThreadId: "thread_mock_agent_atlas",
  }),
  baseAgent({
    id: NOVA_ID,
    name: "Nova",
    role: "İçerik & sosyal",
    description: "Post taslakları, takvim, design ekibi koordinasyonu.",
    avatar: "/brand/agents/pink.png",
    autonomy: "read",
    status: "paused",
    allowedApps: ["slack", "vault", "linkedin"],
    homeThreadId: "thread_mock_agent_nova",
  }),
];

const MOCK_CONTENT_BY_ID: Record<string, MockAgentContent> = {
  [MOCK_DEMO_AGENT_ID]: MIRA_CONTENT,
  [DENIZ_ID]: DENIZ_CONTENT,
  [ECE_ID]: ECE_CONTENT,
  [ATLAS_ID]: ATLAS_CONTENT,
  [NOVA_ID]: NOVA_CONTENT,
};

/** @deprecated use MOCK_AGENTS[0] */
export const MOCK_DEMO_AGENT = MOCK_AGENTS[0]!;

export const MOCK_DEMO_AGENT_MESSAGES = MIRA_CONTENT.messages;

export const MOCK_DEMO_ORCHESTRATION_ACTIVITIES = MIRA_CONTENT.activities;

export function isMockAgentId(agentId: string): boolean {
  return agentId in MOCK_CONTENT_BY_ID;
}

/** @deprecated use isMockAgentId */
export function isMockDemoAgentId(agentId: string): boolean {
  return isMockAgentId(agentId);
}

export function getMockAgent(agentId: string): Agent | null {
  return MOCK_AGENTS.find((a) => a.id === agentId) ?? null;
}

export function getMockAgentSeedMessages(agentId: string): readonly MockAgentMessage[] {
  return MOCK_CONTENT_BY_ID[agentId]?.messages ?? [];
}

export function getMockAgentOrchestrationActivities(
  agentId: string,
): readonly OrchestrationThreadActivity[] {
  return MOCK_CONTENT_BY_ID[agentId]?.activities ?? [];
}

export function getMockAgentRuns(agentId: string): readonly AgentRun[] {
  return MOCK_CONTENT_BY_ID[agentId]?.runs ?? [];
}

export function withMockAgents(agents: readonly Agent[], mockMode: boolean): readonly Agent[] {
  if (!mockMode) return agents;
  const existing = new Set(agents.map((a) => a.id));
  const toAdd = MOCK_AGENTS.filter((a) => !existing.has(a.id));
  if (toAdd.length === 0) return agents;
  return [...toAdd, ...agents];
}

export function withMockAgentRuns(
  runs: readonly AgentRun[],
  mockMode: boolean,
): readonly AgentRun[] {
  if (!mockMode) return runs;
  const existing = new Set(runs.map((r) => r.id));
  const mockRuns = MOCK_AGENTS.flatMap((a) => MOCK_CONTENT_BY_ID[a.id]?.runs ?? []);
  const toAdd = mockRuns.filter((r) => !existing.has(r.id));
  if (toAdd.length === 0) return runs;
  return [...toAdd, ...runs];
}

export function mockMessagesToOrchestration(
  messages: readonly MockAgentMessage[],
): OrchestrationMessage[] {
  return messages
    .filter((m) => m.role !== "system")
    .map((m) => ({
      id: MessageId.make(m.id),
      role: m.role,
      text: m.text,
      turnId: null,
      streaming: m.streaming ?? false,
      createdAt: m.createdAt,
      updatedAt: m.createdAt,
    }));
}

export function mockAgentReplyToUser(text: string, agentName?: string): MockAgentMessage {
  const name = agentName?.trim() || "Agent";
  const MOCK_REPLY = `Mock moddasın — **${name}** gerçek motor olmadan yanıtlıyor. Mesajın kaydedildi.`;
  return {
    id: `mock-msg-${Date.now()}`,
    role: "assistant",
    text: text.trim() ? `${MOCK_REPLY}\n\n> ${text.trim()}` : MOCK_REPLY,
    createdAt: new Date().toISOString(),
  };
}
